import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';

const NotFoundContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 70vh;
  padding: 40px 20px;
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-family: 'Arial', sans-serif;
`;

const NotFoundContent = styled.div`
  background: white;
  padding: 60px 40px;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  max-width: 600px;
`;

const ErrorCode = styled.h1`
  font-size: 120px;
  font-weight: 900;
  margin: 0;
  color: #667eea;
  line-height: 1;
  margin-bottom: 10px;

  @media (max-width: 768px) {
    font-size: 80px;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 32px;
  color: #333;
  margin: 0 0 20px 0;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0 0 30px 0;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const BackButton = styled(Link)`
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 15px 40px;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: none;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 12px 30px;
    font-size: 14px;
  }
`;

const NotFound = () => {
  useEffect(() => {
    // Set 404 status for server-side rendering
    if (window.__RUNTIME_ERROR_PAGE_TYPE) {
      window.__RUNTIME_ERROR_PAGE_TYPE = '404';
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Page Not Found - 404 | Snackster</title>
        <meta name="description" content="The page you are looking for could not be found. Go back to Snackster homepage." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://www.snackster.co.in/" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Page Not Found - 404 | Snackster" />
        <meta property="og:description" content="The page you are looking for could not be found. Go back to Snackster homepage." />
        <meta property="og:url" content="https://www.snackster.co.in/" />
        <meta property="og:image" content="https://www.snackster.co.in/images/snackster_logo.svg" />
        <meta property="og:image:width" content="523" />
        <meta property="og:image:height" content="219" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Page Not Found - 404 | Snackster" />
        <meta name="twitter:description" content="The page you are looking for could not be found. Go back to Snackster homepage." />
        <meta name="twitter:image" content="https://www.snackster.co.in/images/snackster_logo.svg" />
        <meta name="twitter:site" content="@SnehaSnackster" />
      </Helmet>
      <NotFoundContainer>
        <NotFoundContent>
          <ErrorCode>404</ErrorCode>
          <ErrorTitle>Page Not Found</ErrorTitle>
          <ErrorDescription>
            Sorry, the page you are looking for doesn't exist or has been moved. 
            Let's get you back on track!
          </ErrorDescription>
          <BackButton to="/">
            ← Go Back to Home
          </BackButton>
        </NotFoundContent>
      </NotFoundContainer>
    </>
  );
};

export default NotFound;
